"use client";

import { useEffect } from "react";
import { trackEvent } from "@/lib/posthog";

/**
 * Root-layout crash boundary (review WX1). Catches errors thrown by the root
 * `layout.tsx` itself — the regular `error.tsx` cannot, because it renders
 * INSIDE the root layout. Next.js requires this component to render its own
 * `<html>`/`<body>`; the global stylesheet is NOT guaranteed to be present
 * here (the layout that imports it just crashed), so styling is inline.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Same capture pattern as `error.tsx`. PostHog may not be initialized if
  // the crash happened before the provider mounted — `trackEvent` safely
  // no-ops in that case, and console.error still reaches browser/Vercel logs.
  //
  // TODO(server-capture): wire a server-side sink (Sentry / posthog-node)
  // when one is adopted; join on `error.digest`.
  useEffect(() => {
    console.error("[global-error-boundary]", error);
    trackEvent("client_error", {
      digest: error.digest ?? null,
      name: error.name,
      message: error.message?.slice(0, 300) ?? null,
      pathname: typeof window !== "undefined" ? window.location.pathname : null,
      boundary: "global",
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#0A0E1A",
          color: "#F1F5F9",
          fontFamily:
            "'DM Sans', ui-sans-serif, system-ui, -apple-system, sans-serif",
          padding: "0 24px",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>⚠️</div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: "0 0 8px" }}>
            Something Went Wrong
          </h1>
          <p
            style={{
              color: "#94A3B8",
              fontSize: 14,
              lineHeight: 1.6,
              margin: "0 0 24px",
            }}
          >
            An unexpected error occurred. Please try again or contact us if the
            problem persists.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button
              onClick={reset}
              style={{
                backgroundColor: "#3B82F6",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: 14,
                padding: "10px 20px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
              }}
            >
              Try Again
            </button>
            <a
              href="/"
              style={{
                border: "1px solid rgba(148,163,184,0.3)",
                color: "#94A3B8",
                fontWeight: 500,
                fontSize: 14,
                padding: "10px 20px",
                borderRadius: 8,
                textDecoration: "none",
              }}
            >
              Go Home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
